import { useCallback, useEffect, useMemo, useState } from "react";
import type { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components/macro";
import { InvisibleButton, LinkButtonSmallV2 } from "../Buttons/Buttons";
import type { ChipType } from "../Chips/Chips";
import { getFilterItemName, useStoreState } from "../../util/util";
import { CheckBoxNoLabel } from "../CheckBoxes/CheckBoxes";
import { SoftHeader2 } from "../Typography/Typography";
import { XIcon } from "../Icons/Icons";
import { SearchBar } from "../SearchBar/SearchBar";
import type {
  Filters,
  SupportedLanguage,
  TagClassificationConfig,
} from "../../types/types";
import { Loader } from "../Loader/Loader";
import { LoaderContainer } from "../../pages/public/pages.public.util";
import { StringParam, useQueryParams } from "use-query-params";
import { useCookies } from "react-cookie";
import _ from "lodash";
import { CheckBoxFinePrintLabel } from "../Form/Form";

interface AnimatedExpandToggleProps {
  expanded: boolean;
}

const FilterGroupWrapper = styled.div<{ expanded: boolean }>`
  display: block;
  width: 100%;
  position: relative;
  margin: 0px;
  border-bottom: ${({ theme, expanded }) =>
    expanded
      ? `${theme.resolve("border_filterGroup") || 1} solid ${
          theme.resolve("color_filterGroup_border_expanded") ||
          theme.primaryBorder
        }`
      : `${theme.resolve("border_filterGroup") || 1} solid ${
          theme.resolve("color_filterGroup_border_collpased") ||
          theme.primaryBorder
        }`};
  button {
    cursor: pointer;
  }
  transition: max-height 0.25s ease-in;
`;

const FilterGroupHeader = styled.div<{ expanded: boolean }>`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  justify-content: space-between;
  padding: ${({ theme }) =>
    ` ${theme.resolve("spacing_md")} ${theme.resolve("spacing_xl")}`};
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: ${({ theme }) => theme.resolve("font_weight_medium")};
  background: ${({ theme, expanded }) =>
    expanded
      ? theme.resolve("color_filterGroup_bg_expanded")
      : theme.resolve("color_filterGroup_bg_collpased")};
`;

const FilterGroupContent = styled.div`
  background: ${({ theme }) => theme.resolve("color_filterGroup_bg_expanded")};
`;

const SearchBarWrapper = styled.div`
  padding: ${({ theme }) => ` 0 ${theme.resolve("spacing_xl")}`};
  margin-bottom: 10px;
  input {
    border-radius: 4px;
    padding: 10px 13px 10px 35px;
    background: ${({ theme }) => theme.primaryBG};
  }
  svg {
    left: 7px;
    path {
      fill: #000;
    }
  }
`;

const NoSearchResults = styled.div`
  width: 100%;
  padding: 20px 0;
  text-align: center;
`;

const ClearFiltersWrapper = styled.div`
  padding: 0px 0px 0px 6px;
  margin-right: 5px;
  display: flex;
  flex-direction: row;
  align-items: center;
  display: flex;
  border-radius: 3px;
  height: 25px;
  font-size: ${({ theme }) => theme.fontSizes.small};
  line-height: ${({ theme }) => theme.fontSizes.small};
  background: ${({ theme }) => theme.primaryBG};
  border: 1px solid ${({ theme }) => theme.resolve("color_filterPanel_border")};
  button {
    display: flex;
    align-items: center;
  }
`;

const AnimatedExpandToggle = styled.div<AnimatedExpandToggleProps>`
  position: relative;
  width: 25px;
  height: 24px;
  cursor: pointer;
  background: ${({ theme }) => theme.primaryBG};
  &:hover {
    transform: scale(1);
  }
  &:before,
  &:after {
    content: "";
    display: block;
    background-color: #333;
    position: absolute;
    top: 12px;
    left: 6px;
    transition: 0.35s;
    width: 12px;
    height: 2px;
  }
  &:after {
    width: 13px;
  }
  &:before {
    transform: ${({ expanded }) =>
      expanded ? "translatey(-50%)" : "translatey(-50%) rotate(-90deg)"};
    opacity: ${({ expanded }) => (expanded ? "0" : "1")};
    top: ${({ expanded }) => (expanded ? "12px" : "13px")};
    width: ${({ expanded }) => (expanded ? "12px" : "11px")};
    left: ${({ expanded }) => (expanded ? "6px" : "7px")};

    &:after {
      transform: ${({ expanded }) =>
        expanded
          ? "translatey(-50%) rotate(90deg)"
          : "translatey(-50%) rotate(0)"};
    }
  }
`;

const ScrollUL = styled.div`
  max-height: 350px;
  overflow-y: auto;
  display: block;
  padding: 8px 0px 5px 0px;
  transition: max-height 0.3s ease-in;
  li {
    list-style: none;
    padding: ${({ theme }) => ` 0 ${theme.resolve("spacing_xl")}`};
    margin-bottom: ${({ theme }) => theme.resolve("spacing_filterItem_gap")};
    cursor: pointer;
    display: flex;
    flex-direction: row;
    align-items: center;
    transition: all 0.3s ease-in;
    input[type="checkbox"] {
      width: 24px;
      margin-right: ${({ theme }) =>
        theme.resolve("spacing_filterPanel")} !important;
    }
  }
`;

const SelectAllButton = styled(LinkButtonSmallV2)`
  padding: ${({ theme }) => `0 ${theme.resolve("spacing_xl")}`};
`;

interface IFilterBy {
  setter: ({ values, filter }: { values?: ChipType[]; filter: string }) => void;
  options?: ChipType[];
  filters?: Filters;
  filterType: string;
  customLabels: TagClassificationConfig[];
  preselectedFilters: ChipType[];
  expanded: boolean;
  setActiveTagView: (tagView: string) => void;
}

/**
 * Reads in filters from the URL if they exist and applies them the list.
 * selectedFilters will update when a user clicks on the checkbox. When that array changes the request will run.
 */
export const FilterGroup = ({
  setter,
  options,
  filterType,
  preselectedFilters,
  customLabels,
  filters,
  expanded,
  setActiveTagView,
}: IFilterBy) => {
  const [pendingFilters, setPendingFilters] = useState<ChipType[]>(
    preselectedFilters || []
  );

  const [selectedFilters, setSelectedFilters] = useState<ChipType[]>(
    preselectedFilters || []
  );

  const [query, setQuery] = useQueryParams({
    active_tag_view: StringParam,
  });

  const [showList, setShowList] = useState(expanded);
  const [canSelectFilter, setCanSelectFilter] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const theme = useTheme();

  useEffect(() => {
    setShowList(expanded);
  }, [expanded]);

  const { t } = useTranslation();

  const clear = useCallback(() => {
    // useCallback is needed here because this a dep of the
    // below useEffect
    setPendingFilters([]);
    setSelectedFilters([]);
    setter({ values: undefined, filter: filterType });
  }, [setter, filterType]);

  // Memoize filtered options based on search query
  const filteredOptionsBySearch = useMemo(() => {
    if (!searchQuery) return options || [];
    const lowerQuery = searchQuery.toLowerCase();
    return (options || []).filter((item) =>
      item.name.toLowerCase().includes(lowerQuery)
    );
  }, [options, searchQuery]);

  const handleFilterClick = useCallback(
    (option: ChipType, isChecked: boolean) => {
      if (isChecked) {
        setPendingFilters([...pendingFilters, { name: option.name }]);
      } else {
        if (pendingFilters.length > 1) {
          setPendingFilters((prev) =>
            prev.filter((item) => item.name !== option.name)
          );
        } else {
          clear();
        }
      }
    },
    [pendingFilters, clear]
  );

  const handleSelectAll = useCallback(() => {
    setPendingFilters(filteredOptionsBySearch || []);
  }, [filteredOptionsBySearch]);

  const is_all_selected = useMemo(() => {
    return (
      pendingFilters.length > 0 &&
      (filteredOptionsBySearch || []).length > 0 &&
      (filteredOptionsBySearch || []).every((option) =>
        pendingFilters.some((filter) => filter.name === option.name)
      )
    );
  }, [pendingFilters, filteredOptionsBySearch]);

  const { slug: tenantSlug } = useStoreState();

  const [cookies] = useCookies([`preferred-language-${tenantSlug}`]);
  const preferredLanguage: SupportedLanguage | undefined = cookies[
    `preferred-language-${tenantSlug}`
  ] as SupportedLanguage;

  const handleChange = useCallback(
    (val: ChangeEvent<HTMLInputElement>) => setSearchQuery(val.target.value),
    []
  );

  // Memoize the label calculation
  const label = useMemo(() => {
    const getCustomLabel = (filterType: string) => {
      const customLabel = customLabels
        ?.find((customLabel) => customLabel.filter_type === filterType)
        ?.filter_labels?.find(
          (filterLabel) => filterLabel.language === (preferredLanguage ?? "en")
        )?.label;
      return customLabel ? customLabel : getFilterItemName(filterType);
    };
    return getCustomLabel(filterType).toLowerCase();
  }, [filterType, customLabels, preferredLanguage]);

  const handleClearInput = useCallback(() => setSearchQuery(""), []);

  const getFilterName = useCallback(
    (filter: ChipType) => {
      if (searchQuery.length > 0) {
        const lowerName = filter.name.toLowerCase();
        const lowerQuery = searchQuery.toLowerCase();
        const start = lowerName.indexOf(lowerQuery);

        if (start > -1) {
          const end = start + searchQuery.length;
          return (
            <>
              {filter.name.substring(0, start)}
              <b>{filter.name.substring(start, end)}</b>
              {filter.name.substring(end, filter.name.length)}
            </>
          );
        }
      }
      return filter.name;
    },
    [searchQuery]
  );

  // Consolidate preselected filters effect
  useEffect(() => {
    if (preselectedFilters) {
      setSelectedFilters(preselectedFilters);
      setPendingFilters(preselectedFilters);
    }
  }, [preselectedFilters]);

  useEffect(() => {
    setCanSelectFilter(true);
  }, [filters]);

  // Optimize the debounced filter application
  useEffect(() => {
    if (!_.isEqual(pendingFilters, selectedFilters)) {
      const delayApplyFilters = setTimeout(() => {
        setSelectedFilters(pendingFilters);
        setter({
          values: pendingFilters,
          filter: filterType,
        });
        if (query.active_tag_view !== filterType) {
          setQuery({ active_tag_view: filterType });
        }
        setCanSelectFilter(false);
      }, 1500);

      return () => clearTimeout(delayApplyFilters);
    }
  }, [
    pendingFilters,
    setter,
    filterType,
    selectedFilters,
    query.active_tag_view,
    setQuery,
  ]);

  // Memoize click handlers
  const handleExpandToggleClick = useCallback(() => {
    setShowList(!showList);
    setActiveTagView(filterType);
  }, [filterType, setActiveTagView, showList]);

  const handleClearClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      clear();
    },
    [clear]
  );

  return (
    <FilterGroupWrapper expanded={showList}>
      <FilterGroupHeader expanded={showList}>
        <div
          onClick={handleExpandToggleClick}
          style={{ width: "100%", padding: "8px 2px 8px 8px" }}
        >
          {label.charAt(0).toUpperCase()}
          {label.slice(1)}
        </div>
        <div style={{ display: "flex" }}>
          {selectedFilters.length > 0 && (
            <ClearFiltersWrapper>
              <div> {selectedFilters.length}</div>
              <InvisibleButton onClick={handleClearClick}>
                <XIcon width={15} height={15} />
              </InvisibleButton>
            </ClearFiltersWrapper>
          )}
          {
            <AnimatedExpandToggle
              style={{
                padding: "8px 8px 8px 2px",
                background: theme.primaryBG,
              }}
              expanded={showList}
              onClick={handleExpandToggleClick}
            />
          }
        </div>
      </FilterGroupHeader>
      {showList && (
        <FilterGroupContent>
          <SearchBarWrapper>
            <SearchBar
              placeHolder={`Search ${label
                .charAt(0)
                .toUpperCase()}${label.slice(1)}`}
              query={searchQuery}
              handleChange={handleChange}
              handleClearInput={handleClearInput}
            />
          </SearchBarWrapper>
          {!filters && (
            <LoaderContainer style={{ marginTop: "30px" }}>
              <Loader isLoading={true} width={22} />
            </LoaderContainer>
          )}

          {filters && (
            <>
              <SelectAllButton
                onClick={is_all_selected ? clear : handleSelectAll}
                disabled={!canSelectFilter}
              >
                {is_all_selected ? t("Unselect All") : t("Select All")}
              </SelectAllButton>
              <ScrollUL>
                {filteredOptionsBySearch.map((option, index) => (
                  <li key={index}>
                    <CheckBoxNoLabel
                      name={`is_filter_checked${index}`}
                      id={`is_filter_checked${filterType}-${index}`}
                      checked={pendingFilters.some(
                        (i) => i.name === option.name
                      )}
                      onChange={(e) =>
                        handleFilterClick(option, e.currentTarget.checked)
                      }
                      disabled={!canSelectFilter}
                    />
                    <CheckBoxFinePrintLabel
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        cursor: !canSelectFilter ? "not-allowed" : "pointer",
                      }}
                      htmlFor={`is_filter_checked${filterType}-${index}`}
                    >
                      <span
                        style={{
                          color: theme.resolve("color_text_secondary"),
                          marginRight: theme.resolve("spacing_md"),
                        }}
                      >
                        {getFilterName(option)}
                      </span>
                      {!(
                        filterType === "status" || filterType === "template"
                      ) && (
                        <span
                          style={{
                            color: theme.resolve("color_text_tertiary"),
                          }}
                        >
                          {(option as { name: string; count: number }).count}
                        </span>
                      )}
                    </CheckBoxFinePrintLabel>
                  </li>
                ))}
                {filteredOptionsBySearch.length === 0 && (
                  <NoSearchResults>
                    <SoftHeader2>{t("No results found")}</SoftHeader2>
                  </NoSearchResults>
                )}
              </ScrollUL>
            </>
          )}
        </FilterGroupContent>
      )}
    </FilterGroupWrapper>
  );
};

FilterGroup.displayName = "FilterGroup";
